import { TimeRangeInterface } from '@twaice-fe/shared/models';
import { TimeRangeEnumDE } from '@twaice-fe/shared/utilities';
import { endOfDay, endOfMonth, startOfDay, startOfMonth, subDays, subHours, subMonths } from 'date-fns';
import { getOffsetMs } from '../date-utils';
import { DataExplorerQueryParams } from './data-explorer-data-points';

/**
 * Extract wall-clock parts for a UTC instant in the given timezone.
 */
export function getTzParts(
  date: Date,
  tz: string
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const val = (type: string) => parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  return {
    year: val('year'),
    month: val('month'),
    day: val('day'),
    hour: val('hour'),
    minute: val('minute'),
    second: val('second'),
  };
}

/**
 * Build a UTC Date that represents a specific wall-clock time in the given TZ.
 * E.g. tzBuild(2026, 4, 21, 0, 0, 0, 'Africa/Asmera') → UTC instant for midnight in Asmera.
 */
export function tzBuild(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  ms: number,
  tz: string
): Date {
  // Create a UTC date with the wall-clock values, then adjust by the TZ offset
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute, second, ms));
  const offset = getOffsetMs(tz, guess);
  const result = new Date(guess.getTime() - offset);
  // Refine: the offset may differ at the result instant (DST edge)
  const refinedOffset = getOffsetMs(tz, result);
  return new Date(guess.getTime() - refinedOffset);
}

/** Midnight (00:00:00.000) in the given TZ for an ISO date string (YYYY-MM-DD) */
export function tzDateFromIsoDate(isoDate: string, tz: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return tzBuild(year, month, day, 0, 0, 0, 0, tz);
}

/** Midnight (00:00:00.000) in the given TZ for the date of the UTC instant. */
export function tzStartOfDay(date: Date, tz: string): Date {
  const p = getTzParts(date, tz);
  return tzBuild(p.year, p.month, p.day, 0, 0, 0, 0, tz);
}

/** End of day (23:59:59.999) in the given TZ for the date of the UTC instant. */
export function tzEndOfDay(date: Date, tz: string): Date {
  const p = getTzParts(date, tz);
  return tzBuild(p.year, p.month, p.day, 23, 59, 59, 999, tz);
}

/** Start of month (1st, 00:00:00.000) in the given TZ for the month of the UTC instant. */
export function tzStartOfMonth(date: Date, tz: string): Date {
  const p = getTzParts(date, tz);
  return tzBuild(p.year, p.month, 1, 0, 0, 0, 0, tz);
}

/** End of month (last day, 23:59:59.999) in the given TZ for the month of the UTC instant. */
export function tzEndOfMonth(date: Date, tz: string): Date {
  const p = getTzParts(date, tz);
  const lastDay = new Date(Date.UTC(p.year, p.month, 0)).getUTCDate();
  return tzBuild(p.year, p.month, lastDay, 23, 59, 59, 999, tz);
}

export function getTimeRangesForDataExplorer(tz?: string): TimeRangeInterface[] {
  const now = new Date();

  if (!tz) {
    return [
      { id: 'lastHour', value: [subHours(now, 1), now], label: 'Last Hour', disabled: false },
      { id: 'today', value: [startOfDay(now), now], label: 'Today', disabled: false },
      { id: 'yesterday', value: [startOfDay(subDays(now, 1)), endOfDay(subDays(now, 1))], label: 'Yesterday', disabled: false },
      { id: 'last24Hours', value: [subHours(now, 24), now], label: 'Last 24 Hours', disabled: false },
      { id: 'last7Days', value: [subDays(now, 7), now], label: 'Last 7 Days', disabled: false },
      { id: 'last30Days', value: [subDays(now, 30), now], label: 'Last 30 Days', disabled: false },
      { id: 'thisMonth', value: [startOfMonth(now), now], label: 'This Month', disabled: false },
      {
        id: 'previousMonth',
        value: [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
        label: 'Previous Month',
        disabled: false,
      },
    ];
  }

  // All calculations use the selected TZ wall-clock to subtract calendar
  // days/hours so that DST shifts are accounted for correctly.
  const nowParts = getTzParts(now, tz);
  const sub = (days: number) =>
    tzBuild(nowParts.year, nowParts.month, nowParts.day - days, nowParts.hour, nowParts.minute, nowParts.second, 0, tz);
  const yesterday = tzBuild(
    nowParts.year,
    nowParts.month,
    nowParts.day - 1,
    nowParts.hour,
    nowParts.minute,
    nowParts.second,
    0,
    tz
  );
  const lastMonthRef = tzBuild(
    nowParts.year,
    nowParts.month - 1,
    nowParts.day,
    nowParts.hour,
    nowParts.minute,
    nowParts.second,
    0,
    tz
  );

  return [
    {
      id: 'lastHour',
      value: [
        tzBuild(nowParts.year, nowParts.month, nowParts.day, nowParts.hour - 1, nowParts.minute, nowParts.second, 0, tz),
        new Date(now),
      ],
      label: 'Last Hour',
      disabled: false,
    },
    { id: 'today', value: [tzStartOfDay(now, tz), new Date(now)], label: 'Today', disabled: false },
    {
      id: 'yesterday',
      value: [tzStartOfDay(yesterday, tz), tzEndOfDay(yesterday, tz)],
      label: 'Yesterday',
      disabled: false,
    },
    {
      id: 'last24Hours',
      value: [
        tzBuild(nowParts.year, nowParts.month, nowParts.day, nowParts.hour - 24, nowParts.minute, nowParts.second, 0, tz),
        new Date(now),
      ],
      label: 'Last 24 Hours',
      disabled: false,
    },
    { id: 'last7Days', value: [sub(7), new Date(now)], label: 'Last 7 Days', disabled: false },
    { id: 'last30Days', value: [sub(30), new Date(now)], label: 'Last 30 Days', disabled: false },
    { id: 'thisMonth', value: [tzStartOfMonth(now, tz), new Date(now)], label: 'This Month', disabled: false },
    {
      id: 'previousMonth',
      value: [tzStartOfMonth(lastMonthRef, tz), tzEndOfMonth(lastMonthRef, tz)],
      label: 'Previous Month',
      disabled: false,
    },
  ];
}

export function convertToDate(timestamp: number): Date {
  // Check if timestamp is in seconds (typically 10 digits) or milliseconds (typically 13 digits)
  return new Date(Math.round(timestamp).toString().length > 10 ? timestamp : timestamp * 1000);
}

export function resolveDeTimeRange(
  queryParams: Pick<DataExplorerQueryParams, 'startDate' | 'endDate' | 'timeRange'>,
  tz?: string
): TimeRangeInterface {
  const availableTimeRanges = getTimeRangesForDataExplorer(tz);

  if (queryParams.timeRange === 'custom') {
    return getTimeRange(undefined, TimeRangeEnumDE.LAST_7_DAYS, availableTimeRanges, {
      id: queryParams.timeRange,
      value: [convertToDate(Number(queryParams.startDate)), convertToDate(Number(queryParams.endDate))],
      label: 'Custom',
    });
  }

  return getTimeRange(undefined, TimeRangeEnumDE.LAST_7_DAYS, availableTimeRanges, {
    id: queryParams.timeRange,
    value: '',
    label: '',
  });
}

/**
 * @param defaultTimeRangeId the store's current default, which may have been written by a
 *   page whose range list differs from `availableTimeRanges`; an id that is not in this list
 *   is ignored rather than silently selecting a different range.
 * @param fallbackTimeRangeId the caller's own default, used when neither the selected nor
 *   the stored id is available here.
 */
export function getTimeRange(
  defaultTimeRangeId: string | undefined,
  fallbackTimeRangeId: string,
  availableTimeRanges: TimeRangeInterface[],
  selectedTimeRange: TimeRangeInterface | undefined
): TimeRangeInterface | undefined {
  let timeRange: TimeRangeInterface | undefined;
  if (selectedTimeRange?.id !== 'custom') {
    timeRange =
      availableTimeRanges.find((range) => range.id === selectedTimeRange?.id) ??
      availableTimeRanges.find((range) => range.id === defaultTimeRangeId) ??
      availableTimeRanges.find((range) => range.id === fallbackTimeRangeId);
  }

  if (selectedTimeRange?.id === 'custom' && selectedTimeRange?.value[0] && selectedTimeRange?.value[1]) {
    timeRange = {
      id: selectedTimeRange.id,
      value: [selectedTimeRange.value[0] as Date, selectedTimeRange.value[1] as Date],
      label: 'Custom',
      disabled: true,
    };
  }

  return timeRange;
}

const TIME_RANGE_ID_TO_DE: Record<string, string> = {
  sevenDays: 'last7Days',
  thirtyDays: 'last30Days',
};

export function mapTimeRangeIdToDE(id: string): string {
  return TIME_RANGE_ID_TO_DE[id] ?? 'custom';
}
